/**
 * 来訪の詳細 `/reception/visits/:visitId`(設計書 §9.2、計画 第 4 段)。
 * 来訪の変更・取消と、来訪者ごとの操作(編集・招待の再送・リンクの再発行・外す・呼び出しの応答・
 * 対面の確認)。ボタンは API の `can` で出し分ける(真実は API 側)。
 * 再送・再発行は二重押しをまとめる(`Idempotency-Key`)。メールの送信そのものは送信待ちの列が行う。
 * 第 7 段: reception:admin は来訪者ごとに個人情報を消せる(`/api/reception/admin/visitors/{id}/anonymize`。
 * 元に戻せない。匿名化済みの来訪者には出さない)。
 */
import { useMutation } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Typography,
  message,
} from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useState } from "react";
import { useParams } from "react-router-dom";

import { ApiError } from "../../lib/api";
import { DetailPage, KeyValueList, Section, StatusTag, TimeAgo } from "../../ui";
import { errorText, useInvalidateReception, useReceptionPerms, useSend } from "./hooks";
import {
  ATTENTION_REASON,
  INVITE_STATUS,
  INVITE_STATUS_HELP,
  MAIL_KIND,
  NOTIFICATION_STATUS,
  VISITOR_STATUS,
  VISIT_STATUS,
  fmtRange,
  newIdemKey,
  useCoreSend,
  useMyLocations,
  useVisit,
  type VisitDetail,
  type VisitorDetail,
} from "./visits";

const { Text } = Typography;

export function VisitDetailPage() {
  const { visitId } = useParams();
  const id = Number(visitId);
  const query = useVisit(Number.isFinite(id) ? id : null);
  const [editing, setEditing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [adding, setAdding] = useState(false);
  const { readOnly } = useReceptionPerms();
  const back = { to: "/reception/visits", label: "来訪の一覧へ戻る" };

  if (query.isError) {
    return (
      <DetailPage back={back} title="来訪" tabs={[]} alert={<Alert type="error" showIcon message={errorText(query.error)} />} />
    );
  }
  const v = query.data;
  if (!v) return <DetailPage back={back} title="読み込み中…" tabs={[]} />;
  const st = VISIT_STATUS[v.status];
  const ro = readOnly;

  return (
    <>
      <DetailPage
        back={back}
        title={v.title || `来訪 #${v.id}`}
        tags={<StatusTag tone={st?.tone ?? "neutral"}>{st?.label ?? v.status}</StatusTag>}
        actions={
          <Space>
            {v.can.edit && (
              <Button disabled={ro} onClick={() => setEditing(true)}>
                変更
              </Button>
            )}
            {v.can.cancel && (
              <Button danger disabled={ro} onClick={() => setCancelling(true)}>
                取り消す
              </Button>
            )}
          </Space>
        }
        alert={
          v.needs_attention_at || !v.can.view_pii ? (
            <Space direction="vertical" style={{ width: "100%" }}>
              {v.needs_attention_at && <AttentionBanner visit={v} />}
              {!v.can.view_pii && (
                <Alert type="info" showIcon message="閲覧の権限です。来訪者の氏名・会社・件名は表示されません。" />
              )}
            </Space>
          ) : undefined
        }
        summary={[
          { label: "日時", value: fmtRange(v.starts_at, v.ends_at) },
          { label: "拠点", value: v.location?.name },
          { label: "担当者", value: v.host?.name ?? undefined, empty: "(不明)" },
          {
            label: "受付",
            value: `${v.visitors.filter((x) => x.checked_in_at).length}/${v.visitors.filter((x) => x.status !== "removed").length}`,
          },
        ]}
        tabs={[
          {
            key: "visitors",
            label: "来訪者",
            children: <Visitors visit={v} onAdd={() => setAdding(true)} />,
          },
          {
            key: "summary",
            label: "概要",
            children: (
              <Section title="来訪">
                <KeyValueList
                  items={[
                    { label: "件名", value: v.title ?? "—" },
                    { label: "予定人数", value: v.expected_headcount ?? "—" },
                    { label: "種類", value: v.kind === "walkin" ? "飛び込み" : "手動登録" },
                    { label: "登録", value: <TimeAgo iso={v.created_at} /> },
                    ...(v.cancelled_at
                      ? [
                          { label: "取消", value: <TimeAgo iso={v.cancelled_at} /> },
                          ...(v.cancel_reason !== undefined ? [{ label: "取消の理由", value: v.cancel_reason || "—", wide: true }] : []),
                        ]
                      : []),
                    { label: "メモ", value: v.note || "—", wide: true },
                  ]}
                />
              </Section>
            ),
          },
        ]}
      />
      {editing && <EditVisitModal visit={v} onClose={() => setEditing(false)} />}
      {cancelling && <CancelModal visit={v} onClose={() => setCancelling(false)} />}
      {adding && <VisitorModal visit={v} onClose={() => setAdding(false)} />}
    </>
  );
}

/** 要対応(取消の知らせが届かなかった など)。「来訪者へ直接連絡した」で外す(監査に残る)。 */
function AttentionBanner({ visit }: { visit: VisitDetail }) {
  const act = useAction();
  const { readOnly: ro } = useReceptionPerms();
  const reason = visit.attention_reason ?? "";
  return (
    <Alert
      type="warning"
      showIcon
      message="要対応"
      description={
        <>
          {ATTENTION_REASON[reason] ?? `要対応の来訪です(${reason || "理由不明"})`}
          {visit.needs_attention_at && (
            <>
              {" "}
              <Text type="secondary">
                (<TimeAgo iso={visit.needs_attention_at} />)
              </Text>
            </>
          )}
        </>
      }
      action={
        visit.can.resolve_attention ? (
          <Popconfirm
            title="来訪者へ直接連絡しましたか?"
            description="要対応の表示を外し、記録(監査)に残します。"
            okText="連絡した"
            cancelText="戻る"
            onConfirm={() =>
              act.mutate({
                method: "POST",
                path: `/visits/${visit.id}/attention/resolve`,
                ok: "要対応を外しました",
              })
            }
          >
            <Button disabled={ro} loading={act.isPending}>
              来訪者へ直接連絡した
            </Button>
          </Popconfirm>
        ) : undefined
      }
    />
  );
}

function useAction() {
  const send = useCoreSend();
  const invalidate = useInvalidateReception();
  return useMutation({
    mutationFn: (a: { method: string; path: string; body?: unknown; idem?: string; ok: string }) =>
      send<unknown>(a.method, a.path, a.body, a.idem).then(() => a.ok),
    onSuccess: (ok) => {
      invalidate();
      void message.success(ok);
    },
    onError: (e) => void message.error(errorText(e)),
  });
}

function Visitors({ visit, onAdd }: { visit: VisitDetail; onAdd: () => void }) {
  const act = useAction();
  const { readOnly: ro, canAdmin } = useReceptionPerms();
  const [editing, setEditing] = useState<VisitorDetail | null>(null);
  const [anonymizing, setAnonymizing] = useState<VisitorDetail | null>(null);
  const canEdit = visit.can.edit;
  const rows = visit.visitors;

  const columns = [
    {
      title: "来訪者",
      width: 320,
      render: (_: unknown, x: VisitorDetail) =>
        isAnonymized(x) ? (
          <Space direction="vertical" size={0}>
            <Text type="secondary">来訪者 #{x.id}</Text>
            <StatusTag tone="neutral">個人情報を消去済み</StatusTag>
          </Space>
        ) : x.pii_hidden ? (
          <Text type="secondary">来訪者 #{x.id}</Text>
        ) : (
          <Space direction="vertical" size={0}>
            <Text strong>{x.name}</Text>
            <Text type="secondary">{[x.name_kana, x.company].filter(Boolean).join(" / ")}</Text>
            {x.email && <Text type="secondary">{x.email}</Text>}
          </Space>
        ),
    },
    { title: "状態", width: 110, render: (_: unknown, x: VisitorDetail) => VISITOR_STATUS[x.status] ?? x.status },
    {
      title: "招待",
      width: 280,
      render: (_: unknown, x: VisitorDetail) => (
        <Space direction="vertical" size={0}>
          <span title={x.send_invite ? INVITE_STATUS_HELP[x.invite_status] : undefined}>
            {x.send_invite ? (INVITE_STATUS[x.invite_status] ?? x.invite_status ?? "—") : "送らない"}
            {!x.has_email && x.send_invite && <Text type="warning">(メールなし)</Text>}
          </span>
          {x.last_mail && (
            <Text type="secondary">
              最後: {MAIL_KIND[x.last_mail.kind] ?? x.last_mail.kind}・{NOTIFICATION_STATUS[x.last_mail.status] ?? x.last_mail.status}
            </Text>
          )}
          <Text type="secondary">リンク 第 {x.current_generation} 世代</Text>
        </Space>
      ),
    },
    {
      title: "受付",
      width: 160,
      render: (_: unknown, x: VisitorDetail) =>
        x.checked_in_at ? (
          <Space direction="vertical" size={0}>
            <TimeAgo iso={x.checked_in_at} />
            {x.assurance === "verified" ? (
              <StatusTag tone="success">本人確認済み</StatusTag>
            ) : x.assurance === "claimed" ? (
              <StatusTag tone="warning">名乗りのみ</StatusTag>
            ) : null}
            {x.active_call && (
              <Text type="secondary">呼び出し: {x.active_call.acked_at ? "応答済み" : x.active_call.status}</Text>
            )}
          </Space>
        ) : (
          "—"
        ),
    },
    {
      title: "",
      width: 720,
      render: (_: unknown, x: VisitorDetail) => {
        const removed = x.status === "removed";
        const call = x.active_call;
        const callOpen = call && !call.acked_at && (call.status === "calling" || call.status === "escalated");
        const claimed = x.checked_in_at && x.assurance === "claimed";
        return (
          <Space wrap size={4}>
            {canEdit && !removed && (
              <Button size="small" disabled={ro} onClick={() => setEditing(x)}>
                編集
              </Button>
            )}
            {canEdit && !removed && x.send_invite && x.has_email && !x.checked_in_at && (
              <Popconfirm
                title={x.delivery_available ? "同じリンクで招待を送り直します" : "リンクの期限が切れています。再発行してください"}
                okButtonProps={{ disabled: !x.delivery_available }}
                onConfirm={() =>
                  act.mutate({ method: "POST", path: `/visitors/${x.id}/resend`, idem: newIdemKey(), ok: "再送を受け付けました" })
                }
              >
                <Button size="small" disabled={ro}>
                  招待を再送
                </Button>
              </Popconfirm>
            )}
            {canEdit && !removed && !x.checked_in_at && (
              <Popconfirm
                title="今のリンク(QR・コード)を無効にして、新しいリンクを作ります"
                onConfirm={() =>
                  act.mutate({
                    method: "POST",
                    path: `/visitors/${x.id}/link-regenerate`,
                    idem: newIdemKey(),
                    ok: "リンクを再発行しました",
                  })
                }
              >
                <Button size="small" disabled={ro}>
                  リンクを再発行
                </Button>
              </Popconfirm>
            )}
            {canEdit && !removed && !x.checked_in_at && (
              <Popconfirm
                title="この来訪者を外します(リンクは無効になり、招待済みなら取消のメールが届きます)"
                onConfirm={() => act.mutate({ method: "POST", path: `/visitors/${x.id}/remove`, ok: "来訪者を外しました" })}
              >
                <Button size="small" danger disabled={ro}>
                  外す
                </Button>
              </Popconfirm>
            )}
            {visit.can.ack && callOpen && (
              <Button
                size="small"
                type="primary"
                disabled={ro}
                onClick={() => act.mutate({ method: "POST", path: `/visitors/${x.id}/ack`, ok: "応答しました" })}
              >
                呼び出しに応答
              </Button>
            )}
            {claimed && visit.can.verify_operator && (
              <Button
                size="small"
                disabled={ro}
                onClick={() =>
                  act.mutate({
                    method: "POST",
                    path: `/visitors/${x.id}/verify`,
                    body: { method: "operator_in_person" },
                    ok: "本人確認済みにしました",
                  })
                }
              >
                受付で確認
              </Button>
            )}
            {claimed && visit.can.verify_host && (
              <Button
                size="small"
                disabled={ro}
                onClick={() =>
                  act.mutate({
                    method: "POST",
                    path: `/visitors/${x.id}/verify`,
                    body: { method: "host_in_person" },
                    ok: "本人確認済みにしました",
                  })
                }
              >
                担当者が確認
              </Button>
            )}
            {canAdmin && !isAnonymized(x) && (
              <Button size="small" danger disabled={ro} onClick={() => setAnonymizing(x)}>
                個人情報を消す
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <Section
      title="来訪者"
      extra={
        visit.can.add_visitor ? (
          <Button size="small" disabled={ro} onClick={onAdd}>
            来訪者を追加
          </Button>
        ) : undefined
      }
    >
      <Table
        size="small"
        rowKey="id"
        pagination={false}
        columns={columns}
        dataSource={rows}
        rowClassName={(x) => (x.status === "removed" ? "ui-row-muted" : "")}
        locale={{ emptyText: "来訪者はまだいません" }}
        scroll={{ x: "max-content" }}
      />
      {editing && <VisitorModal visit={visit} visitor={editing} onClose={() => setEditing(null)} />}
      {anonymizing && <AnonymizeModal visitor={anonymizing} onClose={() => setAnonymizing(null)} />}
    </Section>
  );
}

/** 匿名化済みか。`anonymized_at` が来ない版のサーバーでも、見える権限なのに氏名が空なら消したものとみなす */
function isAnonymized(x: VisitorDetail): boolean {
  return Boolean(x.anonymized_at) || (!x.pii_hidden && !x.name);
}

const ANONYMIZE_REASONS = [
  { value: "visitor_request", label: "来訪者から消去の依頼があった" },
  { value: "mistaken_entry", label: "誤って登録した" },
  { value: "other", label: "その他" },
];

/** 来訪者の個人情報を消す(reception:admin。元に戻せない)。 */
function AnonymizeModal({ visitor, onClose }: { visitor: VisitorDetail; onClose: () => void }) {
  const send = useSend();
  const invalidate = useInvalidateReception();
  const [reason, setReason] = useState<string | undefined>(undefined);
  const run = useMutation({
    mutationFn: () =>
      send<{ visitor_id: number; anonymized_at: string }>("POST", `/visitors/${visitor.id}/anonymize`, { reason }),
    onSuccess: () => {
      invalidate();
      void message.success("個人情報を消しました");
      onClose();
    },
  });
  const err = run.error;
  const errMsg =
    err instanceof ApiError && err.status === 409
      ? "この来訪者へのメールを送信中です。少し待ってからやり直してください。"
      : err
        ? errorText(err)
        : null;
  const label = visitor.name ? `「${visitor.name}」` : `来訪者 #${visitor.id}`;
  return (
    <Modal
      open
      title="個人情報を消す"
      onCancel={onClose}
      okText="消す"
      okButtonProps={{ danger: true, disabled: !reason }}
      cancelText="戻る"
      confirmLoading={run.isPending}
      onOk={() => run.mutate()}
    >
      {errMsg && <Alert type="error" showIcon style={{ marginBottom: 12 }} message={errMsg} />}
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
        message={`${label}の氏名・ふりがな・会社・メールを消します。元に戻せません。`}
        description="来訪の記録(日時・拠点・受付の状態)は残ります。"
      />
      <Select
        style={{ width: "100%" }}
        placeholder="理由を選ぶ"
        options={ANONYMIZE_REASONS}
        value={reason}
        onChange={setReason}
      />
    </Modal>
  );
}

type VisitForm = {
  title?: string;
  note?: string;
  expected_headcount?: number | null;
  range: [Dayjs, Dayjs];
  location_id: number;
};

function EditVisitModal({ visit, onClose }: { visit: VisitDetail; onClose: () => void }) {
  const send = useCoreSend();
  const invalidate = useInvalidateReception();
  const locations = useMyLocations();
  const [form] = Form.useForm<VisitForm>();
  const save = useMutation({
    mutationFn: (f: VisitForm) => {
      const body: Record<string, unknown> = {};
      if (visit.can.view_pii) {
        if ((f.title ?? "") !== (visit.title ?? "")) body.title = f.title ?? "";
        if ((f.note ?? "") !== (visit.note ?? "")) body.note = f.note ?? "";
      }
      if ((f.expected_headcount ?? null) !== visit.expected_headcount && f.expected_headcount) {
        body.expected_headcount = f.expected_headcount;
      }
      if (visit.can.reschedule) {
        if (!f.range[0].isSame(dayjs(visit.starts_at)) || !f.range[1].isSame(dayjs(visit.ends_at))) {
          body.starts_at = f.range[0].toISOString();
          body.ends_at = f.range[1].toISOString();
        }
        if (f.location_id !== visit.location_id) body.location_id = f.location_id;
      }
      return send<VisitDetail>("PATCH", `/visits/${visit.id}`, body);
    },
    onSuccess: () => {
      invalidate();
      void message.success("来訪を変更しました");
      onClose();
    },
  });
  const locOptions = (locations.data?.items ?? []).map((l) => ({ value: l.id, label: l.name }));

  return (
    <Modal open title="来訪を変更" width={640} onCancel={onClose} okText="保存" confirmLoading={save.isPending} onOk={() => form.submit()}>
      {save.isError && <Alert type="error" showIcon style={{ marginBottom: 12 }} message={errorText(save.error)} />}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="時刻だけの変更は今のリンクのまま日時変更のお知らせを送ります。日付・拠点を変えると新しいリンクになり、前のリンクは使えなくなります。"
      />
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          title: visit.title ?? "",
          note: visit.note ?? "",
          expected_headcount: visit.expected_headcount,
          range: [dayjs(visit.starts_at), dayjs(visit.ends_at)],
          location_id: visit.location_id,
        }}
        onFinish={(f) => save.mutate(f)}
      >
        <Form.Item name="range" label="日時">
          <DatePicker.RangePicker showTime={{ format: "HH:mm", minuteStep: 5 }} format="YYYY-MM-DD HH:mm" disabled={!visit.can.reschedule} />
        </Form.Item>
        <Form.Item name="location_id" label="拠点">
          <Select style={{ width: 240 }} options={locOptions} disabled={!visit.can.reschedule} />
        </Form.Item>
        {visit.can.view_pii && (
          <>
            <Form.Item name="title" label="件名">
              <Input maxLength={200} />
            </Form.Item>
            <Form.Item name="note" label="メモ(受付向け)">
              <Input.TextArea rows={2} maxLength={2000} />
            </Form.Item>
          </>
        )}
        <Form.Item name="expected_headcount" label="予定人数">
          <InputNumber min={1} max={1000} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function CancelModal({ visit, onClose }: { visit: VisitDetail; onClose: () => void }) {
  const send = useCoreSend();
  const invalidate = useInvalidateReception();
  const [reason, setReason] = useState("");
  const cancel = useMutation({
    mutationFn: () => send("POST", `/visits/${visit.id}/cancel`, { reason }),
    onSuccess: () => {
      invalidate();
      void message.success("来訪を取り消しました");
      onClose();
    },
  });
  return (
    <Modal
      open
      title="来訪を取り消す"
      onCancel={onClose}
      okText="取り消す"
      okButtonProps={{ danger: true }}
      confirmLoading={cancel.isPending}
      onOk={() => cancel.mutate()}
    >
      {cancel.isError && <Alert type="error" showIcon style={{ marginBottom: 12 }} message={errorText(cancel.error)} />}
      <p>すべての来訪者のリンクが使えなくなります。招待が届いた可能性のある来訪者には取消のメールを送ります。</p>
      <Input.TextArea rows={3} maxLength={2000} placeholder="理由(社内向け。来訪者には送らない)" value={reason} onChange={(e) => setReason(e.target.value)} />
    </Modal>
  );
}

type VisitorForm = {
  name: string;
  name_kana?: string;
  company?: string;
  email?: string;
  send_invite: boolean;
  consent_declared?: boolean;
};

/** 来訪者の追加(`visitor` なし)と編集。 */
function VisitorModal({ visit, visitor, onClose }: { visit: VisitDetail; visitor?: VisitorDetail; onClose: () => void }) {
  const send = useCoreSend();
  const invalidate = useInvalidateReception();
  const [form] = Form.useForm<VisitorForm>();
  const [idem] = useState(newIdemKey);
  const save = useMutation({
    mutationFn: (f: VisitorForm) => {
      if (!visitor) {
        return send("POST", `/visits/${visit.id}/visitors`, {
          name: f.name,
          name_kana: f.name_kana ?? "",
          company: f.company ?? "",
          email: f.email ? f.email : null,
          send_invite: f.send_invite,
          consent_declared: f.consent_declared ?? false,
        }, idem);
      }
      const body: Record<string, unknown> = {};
      if (f.name !== visitor.name) body.name = f.name;
      if ((f.name_kana ?? "") !== (visitor.name_kana ?? "")) body.name_kana = f.name_kana ?? "";
      if ((f.company ?? "") !== (visitor.company ?? "")) body.company = f.company ?? "";
      if ((f.email ?? "") !== (visitor.email ?? "")) body.email = f.email ? f.email : null;
      if (f.send_invite !== visitor.send_invite) body.send_invite = f.send_invite;
      return send("PATCH", `/visitors/${visitor.id}`, body);
    },
    onSuccess: () => {
      invalidate();
      void message.success(visitor ? "来訪者を変更しました" : "来訪者を追加しました");
      onClose();
    },
  });

  return (
    <Modal
      open
      title={visitor ? "来訪者を編集" : "来訪者を追加"}
      onCancel={onClose}
      okText="保存"
      confirmLoading={save.isPending}
      onOk={() => form.submit()}
    >
      {save.isError && <Alert type="error" showIcon style={{ marginBottom: 12 }} message={errorText(save.error)} />}
      {visitor && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="メールを変えると新しいリンクになり、前のリンクは使えなくなります。"
        />
      )}
      <Form
        form={form}
        layout="vertical"
        initialValues={
          visitor
            ? {
                name: visitor.name ?? "",
                name_kana: visitor.name_kana ?? "",
                company: visitor.company ?? "",
                email: visitor.email ?? "",
                send_invite: visitor.send_invite,
              }
            : { send_invite: true }
        }
        onFinish={(f) => save.mutate(f)}
      >
        <Form.Item name="name" label="氏名" rules={[{ required: true, whitespace: true, message: "氏名" }]}>
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item name="name_kana" label="カナ">
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item name="company" label="会社">
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item name="email" label="メール(招待の宛先)" rules={[{ type: "email", message: "メールの形" }]}>
          <Input />
        </Form.Item>
        <Form.Item name="send_invite" valuePropName="checked">
          <Checkbox>招待を送る</Checkbox>
        </Form.Item>
        {!visitor && (
          <Form.Item name="consent_declared" valuePropName="checked">
            <Checkbox>同意を口頭で得た(本人の同意は受付の端末でも取る)</Checkbox>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
